<template>
  <!-- AI F §1.1.5 — details panel: full metadata, truncated content preview and the actions the role may use -->
  <div
    class="fixed top-0 rtl:left-0 ltr:right-0 h-full w-[420px] bg-white border-l border-slate-100 shadow-lg overflow-auto p-5 z-50"
  >
    <div class="flex flex-row items-start gap-2 mb-4">
      <h3 class="flex-1 text-lg font-medium break-words">{{ source.name }}</h3>
      <!-- AI F §1.2.5, §1.5.4, §1.5.5 — Edit metadata -->
      <woot-button
        v-if="canEdit"
        variant="clear"
        color-scheme="secondary"
        icon="edit"
        size="small"
        @click="$emit('edit')"
      />
      <!-- AI F §1.1.8 — delete is archive, and it can be undone for 30 days -->
      <woot-button
        v-if="canDelete && source.status !== 'archived'"
        v-tooltip.top="$t('KNOWLEDGE_BASE.DETAILS.ARCHIVE_HINT')"
        variant="clear"
        color-scheme="alert"
        icon="archive"
        size="small"
        @click="$emit('archive')"
      />
      <woot-button
        v-if="canDelete && source.status === 'archived'"
        variant="clear"
        color-scheme="secondary"
        icon="arrow-undo"
        size="small"
        @click="$emit('restore')"
      >
        {{ $t('KNOWLEDGE_BASE.DETAILS.RESTORE') }}
      </woot-button>
      <woot-button
        variant="clear"
        color-scheme="secondary"
        icon="dismiss"
        size="small"
        @click="$emit('close')"
      />
    </div>

    <status-badge :status="source.status" class="mb-4" />
    <p v-if="source.error_message" class="text-sm text-red-700 my-3">
      {{ source.error_message }}
    </p>

    <dl class="text-sm">
      <div
        v-for="row in rows"
        :key="row.label"
        class="flex gap-2 py-1.5 border-b border-slate-50"
      >
        <dt class="w-40 text-slate-500">
          {{ $t(`KNOWLEDGE_BASE.DETAILS.${row.label}`) }}
        </dt>
        <dd class="flex-1 break-all">
          {{ clipped(row) }}
          <div v-if="isClipped(row)" class="mt-1">
            <woot-button
              variant="clear"
              size="tiny"
              @click="showFullValue = !showFullValue"
            >
              {{
                $t(
                  `KNOWLEDGE_BASE.DETAILS.${
                    showFullValue ? 'SHOW_LESS' : 'SHOW_MORE'
                  }`
                )
              }}
            </woot-button>
          </div>
        </dd>
      </div>
    </dl>

    <!-- AI F §1.2.5 — the extracted text of a file is editable; saving it re-indexes the source -->
    <template v-if="source.source_type === 'file'">
      <div class="flex items-center gap-2 mt-5 mb-2">
        <h4 class="flex-1 text-sm font-medium">
          {{ $t('KNOWLEDGE_BASE.DETAILS.CONTENT') }}
        </h4>
        <woot-button
          v-if="canReread"
          v-tooltip.top="$t('KNOWLEDGE_BASE.DETAILS.REINDEX_HINT')"
          variant="clear"
          size="small"
          icon="arrow-clockwise"
          @click="$emit('reindex')"
        >
          {{ $t('KNOWLEDGE_BASE.DETAILS.REINDEX') }}
        </woot-button>
        <!-- AI F §1.1.2, §1.2.3 — the original is kept, so a bad extraction can be redone from it -->
        <woot-button
          v-if="canReread"
          v-tooltip.top="$t('KNOWLEDGE_BASE.DETAILS.REEXTRACT_HINT')"
          variant="clear"
          size="small"
          icon="document"
          @click="$emit('reextract')"
        >
          {{ $t('KNOWLEDGE_BASE.DETAILS.REEXTRACT') }}
        </woot-button>
      </div>
      <p v-if="source.content_truncated" class="text-xs text-slate-500 mb-2">
        {{ $t('KNOWLEDGE_BASE.DETAILS.TOO_LONG_TO_EDIT') }}
      </p>
      <textarea
        v-model="content"
        rows="12"
        class="text-xs"
        :disabled="!canReread || source.content_truncated"
      />
      <woot-button
        v-if="canReread && !source.content_truncated"
        size="small"
        :is-disabled="content === source.content"
        @click="$emit('save-content', content)"
      >
        {{ $t('KNOWLEDGE_BASE.DETAILS.SAVE_CONTENT') }}
      </woot-button>
    </template>

    <!-- AI F §1.3.6 — a crawled site shows its text view only; Re-Index fetches the pages again -->
    <template v-if="source.source_type === 'website_url'">
      <div class="flex items-center gap-2 mt-5 mb-2">
        <h4 class="flex-1 text-sm font-medium">
          {{ $t('KNOWLEDGE_BASE.DETAILS.CONTENT') }}
        </h4>
        <woot-button
          v-if="canReread"
          variant="clear"
          size="small"
          icon="arrow-clockwise"
          @click="$emit('reindex')"
        >
          {{ $t('KNOWLEDGE_BASE.DETAILS.RECRAWL') }}
        </woot-button>
      </div>
      <p class="text-xs text-slate-500">
        {{ $t('KNOWLEDGE_BASE.DETAILS.VIEW_ONLY') }}
      </p>
    </template>

    <!-- AI F §1.4.4 — a live search shows its query and its text view only; Re-run reads the page again -->
    <template v-if="source.source_type === 'web_search'">
      <div class="flex items-center gap-2 mt-5 mb-2">
        <h4 class="flex-1 text-sm font-medium">
          {{ $t('KNOWLEDGE_BASE.DETAILS.CONTENT') }}
        </h4>
        <woot-button
          v-if="canReread"
          variant="clear"
          size="small"
          icon="arrow-clockwise"
          @click="$emit('reindex')"
        >
          {{ $t('KNOWLEDGE_BASE.DETAILS.RERUN_SEARCH') }}
        </woot-button>
      </div>
      <p class="text-xs text-slate-500">
        {{ $t('KNOWLEDGE_BASE.DETAILS.TEMPORARY') }}
      </p>
    </template>

    <!-- AI F §1.5.4 — a custom answer shows its patterns and its response instead of extracted text -->
    <template v-if="source.source_type === 'custom_answer'">
      <h4 class="mt-5 mb-2 text-sm font-medium">
        {{ $t('KNOWLEDGE_BASE.DETAILS.QUESTIONS') }}
      </h4>
      <ul class="text-sm list-disc ltr:pl-5 rtl:pr-5">
        <li v-for="question in settings.questions || []" :key="question">
          {{ question }}
        </li>
        <li
          v-for="rule in settings.keywords || []"
          :key="rule"
          class="text-slate-500"
        >
          {{ $t('KNOWLEDGE_BASE.DETAILS.KEYWORDS') }}: {{ rule }}
        </li>
      </ul>
      <h4 class="mt-5 mb-2 text-sm font-medium">
        {{ $t('KNOWLEDGE_BASE.DETAILS.ANSWER') }}
      </h4>
      <!-- AI F §1.5.1 — bold, links and line breaks are kept, so the panel shows what the customer will see -->
      <div
        v-dompurify-html="formatMessage(settings.answer || '', false)"
        class="text-sm answer-body"
      />
    </template>

    <template v-if="source.content_preview">
      <h4 class="mt-5 mb-2 text-sm font-medium">
        {{ $t('KNOWLEDGE_BASE.DETAILS.PREVIEW') }}
      </h4>
      <pre
        class="whitespace-pre-wrap text-xs bg-slate-25 p-3 rounded max-h-96 overflow-auto"
        >{{ source.content_preview }}</pre
      >
    </template>
  </div>
</template>

<script>
import { formatSize } from './helpers';
import timeMixin from '../../../../mixins/time';
import hasPermission from '../../../../mixins/hasPermission';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import StatusBadge from './StatusBadge';

const CLIP_CHARS = 30;

export default {
  components: { StatusBadge },
  mixins: [timeMixin, hasPermission, messageFormatterMixin],
  props: {
    source: {
      type: Object,
      required: true,
    },
  },
  data() {
    return { content: this.source.content || '', showFullValue: false };
  },
  computed: {
    settings() {
      return this.source.settings || {};
    },
    canEdit() {
      return this.hasEditPermission('Knowledge Base');
    },
    // AI F §1.1.8 — re-reading an archived source would put it back into search
    canReread() {
      return this.canEdit && this.source.status !== 'archived';
    },
    // AI F §1.1.8, §1.2.6, §1.3.7, §1.4.5 — archive and restore need the delete permission
    canDelete() {
      return this.hasDeletePermission('Knowledge Base');
    },
    rows() {
      return [
        { label: 'ID', value: this.source.uuid },
        {
          label: 'TYPE',
          value: this.$t(
            `KNOWLEDGE_BASE.TYPES.${this.source.source_type.toUpperCase()}`
          ),
        },
        { label: 'SIZE', value: this.formatSize(this.source.size_bytes) },
        { label: 'CREATED_AT', value: this.formatTime(this.source.created_at) },
        {
          label: 'LAST_UPDATED',
          value: this.formatTime(this.source.content_updated_at),
        },
        {
          label: 'CREATED_BY',
          value: this.source.created_by ? this.source.created_by.name : '—',
        },
        {
          label: 'UPDATED_BY',
          value: this.source.updated_by ? this.source.updated_by.name : '—',
        },
        // AI F §1.3.6 — the crawled address and its scope belong in the panel
        ...(this.source.source_type === 'website_url'
          ? [
              { label: 'URL', value: this.settings.url, clip: true },
              {
                label: 'SCOPE',
                value: this.$t(
                  `KNOWLEDGE_BASE.URL.SCOPES.${(
                    this.settings.scope || 'page'
                  ).toUpperCase()}`
                ),
              },
              { label: 'PAGES', value: this.settings.pages_crawled || '—' },
              { label: 'CREDITS', value: this.settings.credits_used || '—' },
            ]
          : []),
        // AI F §1.4.4 — target URL, the query and when the temporary result is discarded
        // AI F §1.1.8 — an archived source says when it will be deleted for good
        ...(this.source.archived_at
          ? [
              {
                label: 'ARCHIVED_AT',
                value: this.formatTime(this.source.archived_at),
              },
            ]
          : []),
        ...(this.source.source_type === 'web_search'
          ? [
              { label: 'URL', value: this.settings.url, clip: true },
              {
                label: 'EXPIRES_AT',
                value: this.formatTime(this.source.expires_at),
              },
              { label: 'CREDITS', value: this.settings.credits_used || '—' },
            ]
          : []),
      ];
    },
  },
  watch: {
    // AI F §1.2.3, §1.2.5 — the panel is not rebuilt for the same source, so the textarea holds a copy
    'source.content': function syncContent(text) {
      this.content = text || '';
    },
  },
  methods: {
    formatSize,
    formatTime(timestamp) {
      return timestamp
        ? this.messageStamp(timestamp, 'LLL d, yyyy h:mm a')
        : '—';
    },
    isClipped(row) {
      return Boolean(row.clip) && row.value.length > CLIP_CHARS;
    },
    clipped(row) {
      if (!this.isClipped(row) || this.showFullValue) return row.value;
      return `${row.value.slice(0, CLIP_CHARS)}…`;
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .answer-body {
    ol {
      @apply list-decimal ltr:pl-5 rtl:pr-5;
    }

    ul {
      @apply list-disc ltr:pl-5 rtl:pr-5;
    }
  }
}
</style>
